import React from "react";
import { Link } from "react-router-dom";
import Seo from "../../components/public/Seo";
import "../../styles/MerchantInfoPage.css";

export default function MerchantInfoPage() {
  return (
    <>
      <Seo
        title="Partner with Veya — When intention leads fashion"
        description="Veya helps independent fashion retailers connect with shoppers through intentional discovery, immersive try-on, and style-led merchant insights."
      />

      <main className="merchantInfoPage">
        <section className="merchantHero">
          <div className="merchantEyebrow">For independent fashion retailers</div>

          <h1>When intention leads fashion.</h1>

          <div className="merchantHeroCopy">
            <p>
              The way we shop has changed. More products, faster fashion,
              endless scrolling — and independent retailers are being drowned
              out by platforms built for volume, not curation.
            </p>

            <p>
              Veya was created to bring intention back into online fashion
              discovery.
            </p>

            <p>
              It gives shoppers a place to slow down, refine their style, and
              interact with products in a more considered way — while helping
              independent boutiques connect with people who are already looking
              for their point of view.
            </p>
          </div>

          <div className="merchantActions">
            <Link to="/merchant/apply" className="merchantButton primary">
              Apply to Partner
            </Link>

            <a href="mailto:hello@veya.co.nz" className="merchantButton secondary">
              Ask a Question
            </a>
          </div>
        </section>

        <section className="merchantImageSection">
          <div className="merchantImageFrame">
            <img
              src="/merchant/merchant-discovery.webp"
              alt="Veya style discovery experience"
            />
          </div>

          <div className="merchantSectionCopy">
            <div className="merchantEyebrow">Why Veya</div>

            <h2>Customers do not shop in single products.</h2>

            <p>
              They imagine outfits. They compare silhouettes. They refine
              identity, occasion, and confidence.
            </p>

            <p>
              Veya allows boutiques to exist inside that process — not just at
              the end of it. Your pieces are discovered through style direction,
              mood, colour, silhouette, and intent.
            </p>
          </div>
        </section>

        <section className="merchantSteps">
          <div className="merchantSectionIntro">
            <div className="merchantEyebrow">The experience</div>

            <h2>A more considered path from discovery to purchase.</h2>

            <p>
              Veya is designed around how shoppers actually think about clothes:
              what they are drawn to, how they want to feel, and how pieces work
              together.
            </p>
          </div>

<div className="merchantStepGrid">
  <article className="merchantStepCard">
    <div className="merchantStepContent">
      <span>01</span>
      <h3>Refine style direction</h3>
      <p>
        Shoppers explore visual edits and save what resonates, building
        a clearer picture of what they are looking for.
      </p>
    </div>
  </article>

  <article className="merchantStepCard">
    <div className="merchantStepContent">
      <span>02</span>
      <h3>Discover aligned pieces</h3>
      <p>
        Products are surfaced through style intent, helping shoppers
        find boutiques that match their taste and point of view.
      </p>
    </div>
  </article>

  <article className="merchantStepCard">
    <div className="merchantStepContent">
      <span>03</span>
      <h3>Interact before buying</h3>
      <p>
        Shoppers can engage with pieces in a fitting room experience,
        then return to the merchant’s own store to purchase.
      </p>
    </div>
  </article>
</div>
        </section>

        <section className="merchantSplitPanel">
          <div>
            <div className="merchantEyebrow">Not a marketplace</div>

            <h2>The boutique stays at the centre.</h2>

            <p>
              Veya is an experience layer, not a replacement for your store.
              Customers discover and style your pieces in Veya, then purchase
              through your own ecommerce site.
            </p>
          </div>

          <div className="merchantPlainList">
            <ul>
              <li>No commissions</li>
              <li>No marketplace checkout</li>
              <li>Your ecommerce remains intact</li>
              <li>Your boutique identity stays visible</li>
            </ul>
          </div>
        </section>

        <section className="merchantInsights">
          <div className="merchantInsightsCopy">
            <div className="merchantEyebrow">Merchant insights</div>

            <h2>See what shoppers are trying to build.</h2>

            <p>
              Most analytics show what happened after a customer found you:
              page views, clicks, sales, abandoned carts.
            </p>

            <p>
              Veya is designed to reveal what happens earlier — what shoppers
              are drawn to, how they style pieces, what they save, what they try
              on, and where interest is building before it becomes a purchase.
            </p>

            <div className="merchantInsightList">
              <div>
                <h3>See what is trending</h3>
                <p>
                  Understand which colours, silhouettes, moods, and categories
                  are gaining attention.
                </p>
              </div>

              <div>
                <h3>Understand styling behaviour</h3>
                <p>
                  See what your pieces are being saved, tried on, or styled with.
                </p>
              </div>

              <div>
                <h3>Find catalogue gaps</h3>
                <p>
                  Identify what shoppers keep looking for, even when you do not
                  currently stock the right piece.
                </p>
              </div>

              <div>
                <h3>Guide better decisions</h3>
                <p>
                  Use customer intent to support buying, merchandising, content,
                  and product presentation decisions.
                </p>
              </div>
            </div>
          </div>

          <div className="merchantInsightsImage">
            <img
              src="/merchant/merchant-insights.webp"
              alt="Example Veya merchant insights dashboard"
            />
          </div>
        </section>

        <section className="merchantProcess">
          <div className="merchantSectionIntro">
            <div className="merchantEyebrow">How it works</div>

            <h2>Built around your existing store.</h2>

            <p>
              Veya supports discovery and confidence before purchase. Your
              ecommerce remains the place where transactions happen.
            </p>
          </div>

          <div className="merchantProcessGrid">
            <article>
              <span>1</span>
              <h3>Connect your catalogue</h3>
              <p>
                Your products are imported into Veya so shoppers can discover
                them in a more visual, style-led environment.
              </p>
            </article>

            <article>
              <span>2</span>
              <h3>Prepare product experiences</h3>
              <p>
                Veya prepares suitable product imagery for discovery, styling,
                and fitting room interaction.
              </p>
            </article>

            <article>
              <span>3</span>
              <h3>Send shoppers back to you</h3>
              <p>
                Shoppers explore in Veya, then purchase through your own online
                store.
              </p>
            </article>
          </div>
        </section>

        <section className="merchantPricing">
          <div className="merchantSectionIntro">
            <div className="merchantEyebrow">Pricing</div>

            <h2>Simple monthly pricing. No commissions.</h2>

            <p>
              Pricing is based on catalogue size. Annual options are available.
              All prices are in NZD.
            </p>
          </div>

          <div className="merchantPriceGrid">
            <article>
              <h3>Under 100 products</h3>
              <strong>$90</strong>
              <span>per month</span>
            </article>

            <article>
              <h3>100–200 products</h3>
              <strong>$130</strong>
              <span>per month</span>
            </article>

            <article>
              <h3>200+ products</h3>
              <strong>$150</strong>
              <span>per month</span>
            </article>
          </div>
        </section>

        <section className="merchantFinalCta">
          <div className="merchantEyebrow">Partner with Veya</div>

          <h2>Bring your boutique into a more intentional shopping experience.</h2>

          <p>
            Apply to partner with Veya, or get in touch first if you would like
            to understand how it could work with your current ecommerce setup.
          </p>

          <div className="merchantActions">
            <Link to="/merchant/apply" className="merchantButton primary">
              Apply to Partner
            </Link>

            <a href="mailto:hello@veya.co.nz" className="merchantButton secondary">
              Contact Veya
            </a>
          </div>
        </section>
      </main>
    </>
  );
}